import { Container, Row, Col, Button, Navbar, Nav } from 'react-bootstrap';
import { MdArrowDownward } from "react-icons/md";
import './HeroChat.css';
import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import { MdContentCopy, MdThumbUp, MdThumbDown, MdMic, MdKeyboardArrowDown } from 'react-icons/md';
import { FaArrowRightLong } from 'react-icons/fa6';
import { useChat } from '../../context/ChatContext';
import WaitlistModal from '../WaitlistModal/WaitlistModal';


const HeroSection = ({ messages = [], // Provide default empty array 
  loading,
  isStreaming,
  onSendMessage, 
  currentBotId, 
  currentScreenId,
  onSwitchBot,
  screens }) => {
  const [input, setInput] = useState('');
  const [hoveredUserMsg, setHoveredUserMsg] = useState(null);
  const [feedback, setFeedback] = useState({}); // { [messageIdx]: 'thanks' }
  const [copied, setCopied] = useState({}); // { [messageIdx]: true }
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const [showNewText, setShowNewText] = useState(true); // Control new text above headline visibility
  const [showWaitlistModal, setShowWaitlistModal] = useState(false); // Control waitlist modal visibility
  const messagesEndRef = useRef(null);
  const messageStartRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const userMessagesStartRef = useRef(null);
  const { currentChatId, updateAssistantMessageContent, chatsByScreen } = useChat();

  // Dropdown examples data
  const dropdownExamples = {
    'dosecalc': [
      "Fentanyl dose for intraoperative analgesia in a 30kg pediatric patient.",
      "Midazolam premedication for a 60-year-old male with liver disease."
    ],
    'case-manager': [
      "65-year-old male, elective total knee replacement, history of hypertension and diabetes.",
      "45-year-old female, laparoscopic cholecystectomy, BMI 42."
    ],
    'search': [
      "Best practices for difficult airway management in obese patients.",
      "How to manage perioperative beta-blockers in non-cardiac surgery."
    ]
  };

  // Function to check if current chat has 5 or more messages
  const checkMessageLimit = () => {
    console.log('messages', messages);
    if (!messages || messages.length === 0) return false;
    
    // Check if current chat has 5 or more messages
    return messages.length >= 10;
  };

  // Effect to hide headline, scroll disclaimer, and new text after 1.2 seconds
  useEffect(() => {
    if (!messages || messages.length === 0) {
      setShowNewText(true);
      const timer = setTimeout(() => {
        setShowNewText(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  // Handle dropdown toggle
  const toggleDropdown = (botId) => {
    setOpenDropdown(openDropdown === botId ? null : botId);
  };

  // Handle example selection
  const handleExampleSelect = (example) => {
    setInput(example);
    setOpenDropdown(null);
    // Check if we can send the message
    if (checkMessageLimit()) {
      setShowWaitlistModal(true);
      return;
    }
    // Send the example to chat immediately
    onSendMessage(example);
    setInput('');
  };

  // Add effect to scroll to last message when a new AI message is added
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    
    // Find the messages container
    const messagesContainer = document.querySelector('.hero-messages');
    if (!messagesContainer) return;
    
    // Scroll to the bottom of the messages container
    const scrollToBottom = () => {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    };
    
    // Use setTimeout to ensure DOM is updated before scrolling
    setTimeout(scrollToBottom, 100);
  }, [messages]);

  // Function to scroll to bottom of messages
  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('.hero-messages');
    if (messagesContainer) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      });
    }
  };

  // Effect to scroll to bottom when messages change
  useEffect(() => {
    if (messages && messages.length > 0) {
      // Use a small delay to ensure all content is rendered
      setTimeout(scrollToBottom, 50);
    }
  }, [messages]);

  // Effect to scroll to bottom when component mounts with messages
  useEffect(() => {
    if (messages && messages.length > 0) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(scrollToBottom, 200);
    }
  }, []); // Empty dependency array means this runs once on mount

  // Effect to scroll to bottom when loading state changes (AI typing)
  useEffect(() => {
    if (loading && messages && messages.length > 0) {
      // Scroll when AI starts typing
      setTimeout(scrollToBottom, 100);
    }
  }, [loading, messages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.hero-action-btn-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    // Check if we've reached the message limit
    if (checkMessageLimit()) {
      setShowWaitlistModal(true);
      return;
    }
    
    onSendMessage(input);
    setInput('');
  };

  // Copy to clipboard handler
  const handleCopy = (content, idx) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(content);
    } else {
      // fallback
      const textarea = document.createElement('textarea');
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(prev => ({ ...prev, [idx]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [idx]: false })), 1200);
  };

  // Like/dislike handler
  const handleFeedback = (idx) => {
    setFeedback(prev => ({ ...prev, [idx]: true }));
    setTimeout(() => setFeedback(prev => ({ ...prev, [idx]: false })), 1500);
  };

  // Function to set hovered message index
  const setHoveredMessage = (idx) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredUserMsg(idx);
  };

  // Function to handle mouse leave with timeout
  const handleMouseLeave = () => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Set new timeout
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredUserMsg(null);
      hoverTimeoutRef.current = null;
    }, 3000);
  };

  // Use the currentScreenId prop if available, otherwise compute it
  const screenId = currentScreenId || (() => {
    if (!screens || !currentBotId) return 'case-manager';
    
    const screen = screens.find(s => s.botId === currentBotId);
    return screen ? screen.id : (screens.length > 0 ? screens[0].id : 'case-manager');
  })();

  // Loading state for screens
  if (!screens) {
    return (
      <div className="hero-chat-area-loading">
        <div className="hero-spinner"></div>
        <div className="hero-loading-text">Loading chat interface</div>
        <WaitlistModal 
          show={showWaitlistModal} 
          onHide={() => setShowWaitlistModal(false)} 
          isInvestorInquiry={false}
        />
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="hero-empty-center-wrapper">
        <div className={`hero-new-text-above-headline ${!showNewText ? 'hidden' : ''}`}>
          <strong>MAIA</strong> - Medical AI-Powered Assistant for Specialty Care <br/>
          Helping clinicians make faster, evidence-based decisions at the point of care
        </div>
        <div className={`hero-empty-chat-headline`}>
          {screenId === 'case-manager' && 'Enter age, surgery, comorbidities, or labs for an anesthesia plan.'}
          {screenId === 'dosecalc' && 'Type medication for recommended doses.'}
          {screenId === 'search' && 'Enter a topic for concise evidence-based summary.'}
        </div>
        <form className="hero-input-card" onSubmit={handleSubmit}>
          <div className="hero-input-row">
            <input
              type="text"
              placeholder="Ask me anything..."
              className="hero-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
            />
            <button type="submit" className="hero-send-btn" disabled={loading || !input.trim()}>
              {loading ? (
                <span className="hero-button-loading">
                  <div className="hero-spinner-small"></div>
                </span>
              ) : (
                <div className='p-3'> 
                  <span className='px-2'>Try</span>
                  <FaArrowRightLong size={16} />
                </div>  
              )}
            </button>
          </div>
          <div className="hero-tab-buttons">
            {screens.map(bot => (
              <button
                type="button"
                key={bot.botId}
                className={`hero-tab-btn${screenId === bot.id ? ' active' : ''}`}
                disabled={loading}
                onClick={() => onSwitchBot(bot.id)}
              >
                <div className="hero-tab-btn-content">
                  {screenId === bot.id ? bot.botImg : bot.botImgWhite}
                  {bot.label}
                </div>
              </button>
            ))}
          </div>
        </form>
        <div className="hero-disclaimer-text align-self-center py-2">
          MAIA is informational, not a substitute for professional judgement.
        </div>
        <div className={`hero-action-buttons ${openDropdown ? 'expanded' : ''}`}>
          <div className="hero-action-btn-container">
            <button className={`hero-action-btn ${openDropdown === 'dosecalc' ? 'expanded' : ''}`} onClick={() => toggleDropdown('dosecalc')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_1086_1068)"><path d="M23.7068 22.2937L17.7378 16.3247C19.3644 14.3353 20.1642 11.7968 19.9716 9.23426C19.7791 6.67173 18.609 4.28123 16.7034 2.55722C14.7977 0.833208 12.3024 -0.0924103 9.73342 -0.0281784C7.16447 0.0360534 4.71849 1.08522 2.9014 2.90231C1.08431 4.7194 0.0351379 7.16539 -0.029094 9.73434C-0.0933258 12.3033 0.832293 14.7987 2.5563 16.7043C4.28031 18.6099 6.67081 19.78 9.23334 19.9725C11.7959 20.1651 14.3344 19.3653 16.3238 17.7387L22.2928 23.7077C22.4814 23.8899 22.734 23.9907 22.9962 23.9884C23.2584 23.9861 23.5092 23.8809 23.6946 23.6955C23.88 23.5101 23.9852 23.2593 23.9875 22.9971C23.9897 22.7349 23.8889 22.4823 23.7068 22.2937ZM9.99978 18.0007C8.41753 18.0007 6.87081 17.5315 5.55522 16.6525C4.23963 15.7734 3.21425 14.524 2.60875 13.0622C2.00324 11.6004 1.84482 9.99182 2.1535 8.43998C2.46218 6.88813 3.22411 5.46266 4.34293 4.34384C5.46175 3.22502 6.88721 2.4631 8.43906 2.15442C9.99091 1.84573 11.5994 2.00416 13.0613 2.60966C14.5231 3.21516 15.7725 4.24054 16.6515 5.55614C17.5306 6.87173 17.9998 8.41845 17.9998 10.0007C17.9974 12.1217 17.1538 14.1551 15.654 15.6549C14.1542 17.1547 12.1208 17.9983 9.99978 18.0007Z" fill="white"/><path d="M13 8.99999H11V7C11 6.73478 10.8946 6.48043 10.7071 6.29289C10.5196 6.10536 10.2652 6 9.99999 6C9.73478 6 9.48042 6.10536 9.29289 6.29289C9.10535 6.48043 8.99999 6.73478 8.99999 7V8.99999H7C6.73478 8.99999 6.48043 9.10535 6.29289 9.29289C6.10536 9.48042 6 9.73478 6 9.99999C6 10.2652 6.10536 10.5196 6.29289 10.7071C6.48043 10.8946 6.73478 11 7 11H8.99999V13C8.99999 13.2652 9.10535 13.5196 9.29289 13.7071C9.48042 13.8946 9.73478 14 9.99999 14C10.2652 14 10.5196 13.8946 10.7071 13.7071C10.8946 13.5196 11 13.2652 11 13V11H13C13.2652 11 13.5196 10.8946 13.7071 10.7071C13.8946 10.5196 14 10.2652 14 9.99999C14 9.73478 13.8946 9.48042 13.7071 9.29289C13.5196 9.10535 13.2652 8.99999 13 8.99999Z" fill="white"/></g><defs><clipPath id="clip0_1086_1068"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>              
              <span>Enter the medication to view recommended dosages.</span>
              <MdKeyboardArrowDown color={'white'} size={16} className={`hero-dropdown-arrow ${openDropdown === 'dosecalc' ? 'rotated' : ''}`} />
            </button>
            {openDropdown === 'dosecalc' && (
              <div className="hero-expanded-examples">
                {dropdownExamples['dosecalc'].map((example, index) => (
                  <button
                    key={index}
                    className="hero-example-item"
                    onClick={() => handleExampleSelect(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="hero-action-btn-container">
            <button className={`hero-action-btn ${openDropdown === 'case-manager' ? 'expanded' : ''}`} onClick={() => toggleDropdown('case-manager')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 0H8C6.67392 0 5.40215 0.526784 4.46447 1.46447C3.52678 2.40215 3 3.67392 3 5V19C3 19.6566 3.12933 20.3068 3.3806 20.9134C3.63188 21.52 4.00017 22.0712 4.46447 22.5355C5.40215 23.4732 6.67392 24 8 24H16C16.6566 24 17.3068 23.8707 17.9134 23.6194C18.52 23.3681 19.0712 22.9998 19.5355 22.5355C19.9998 22.0712 20.3681 21.52 20.6194 20.9134C20.8707 20.3068 21 19.6566 21 19V5C21 3.67392 20.4732 2.40215 19.5355 1.46447C18.5979 0.526784 17.3261 0 16 0V0ZM19 19C19 19.7956 18.6839 20.5587 18.1213 21.1213C17.5587 21.6839 16.7956 22 16 22H8C7.20435 22 6.44129 21.6839 5.87868 21.1213C5.31607 20.5587 5 19.7956 5 19V5C5 4.20435 5.31607 3.44129 5.87868 2.87868C6.44129 2.31607 7.20435 2 8 2H16C16.7956 2 17.5587 2.31607 18.1213 2.87868C18.6839 3.44129 19 4.20435 19 5V19Z" fill="white"/><path d="M14.4167 10.75H12.5833V8.91667C12.5833 8.67355 12.4867 8.44039 12.3148 8.26849C12.1429 8.09658 11.9098 8 11.6667 8C11.4235 8 11.1904 8.09658 11.0185 8.26849C10.8466 8.44039 10.75 8.67355 10.75 8.91667V10.75H8.91667C8.67355 10.75 8.44039 10.8466 8.26849 11.0185C8.09658 11.1904 8 11.4235 8 11.6667C8 11.9098 8.09658 12.1429 8.26849 12.3148C8.44039 12.4867 8.67355 12.5833 8.91667 12.5833H10.75V14.4167C10.75 14.6598 10.8466 14.8929 11.0185 15.0648C11.1904 15.2367 11.4235 15.3333 11.6667 15.3333C11.9098 15.3333 12.1429 15.2367 12.3148 15.0648C12.4867 14.8929 12.5833 14.6598 12.5833 14.4167V12.5833H14.4167C14.6598 12.5833 14.8929 12.4867 15.0648 12.3148C15.2367 12.1429 15.3333 11.9098 15.3333 11.6667C15.3333 11.4235 15.2367 11.1904 15.0648 11.0185C14.8929 10.8466 14.6598 10.75 14.4167 10.75Z" fill="white"/></svg>              <span>Provide age, procedure, medical history, or lab results to generate an anesthesia plan.</span>
              <MdKeyboardArrowDown color={'white'} size={16} className={`hero-dropdown-arrow ${openDropdown === 'case-manager' ? 'rotated' : ''}`} />
            </button>
            {openDropdown === 'case-manager' && (
              <div className="hero-expanded-examples">
                {dropdownExamples['case-manager'].map((example, index) => (
                  <button
                    key={index}
                    className="hero-example-item"
                    onClick={() => handleExampleSelect(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="hero-action-btn-container">
            <button className={`hero-action-btn ${openDropdown === 'search' ? 'expanded' : ''}`} onClick={() => toggleDropdown('search')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_1086_1068)"><path d="M23.7068 22.2937L17.7378 16.3247C19.3644 14.3353 20.1642 11.7968 19.9716 9.23426C19.7791 6.67173 18.609 4.28123 16.7034 2.55722C14.7977 0.833208 12.3024 -0.0924103 9.73342 -0.0281784C7.16447 0.0360534 4.71849 1.08522 2.9014 2.90231C1.08431 4.7194 0.0351379 7.16539 -0.029094 9.73434C-0.0933258 12.3033 0.832293 14.7987 2.5563 16.7043C4.28031 18.6099 6.67081 19.78 9.23334 19.9725C11.7959 20.1651 14.3344 19.3653 16.3238 17.7387L22.2928 23.7077C22.4814 23.8899 22.734 23.9907 22.9962 23.9884C23.2584 23.9861 23.5092 23.8809 23.6946 23.6955C23.88 23.5101 23.9852 23.2593 23.9875 22.9971C23.9897 22.7349 23.8889 22.4823 23.7068 22.2937ZM9.99978 18.0007C8.41753 18.0007 6.87081 17.5315 5.55522 16.6525C4.23963 15.7734 3.21425 14.524 2.60875 13.0622C2.00324 11.6004 1.84482 9.99182 2.1535 8.43998C2.46218 6.88813 3.22411 5.46266 4.34293 4.34384C5.46175 3.22502 6.88721 2.4631 8.43906 2.15442C9.99091 1.84573 11.5994 2.00416 13.0613 2.60966C14.5231 3.21516 15.7725 4.24054 16.6515 5.55614C17.5306 6.87173 17.9998 8.41845 17.9998 10.0007C17.9974 12.1217 17.1538 14.1551 15.654 15.6549C14.1542 17.1547 12.1208 17.9983 9.99978 18.0007Z" fill="white"/><path d="M13 8.99999H11V7C11 6.73478 10.8946 6.48043 10.7071 6.29289C10.5196 6.10536 10.2652 6 9.99999 6C9.73478 6 9.48042 6.10536 9.29289 6.29289C9.10535 6.48043 8.99999 6.73478 8.99999 7V8.99999H7C6.73478 8.99999 6.48043 9.10535 6.29289 9.29289C6.10536 9.48042 6 9.73478 6 9.99999C6 10.2652 6.10536 10.5196 6.29289 10.7071C6.48043 10.8946 6.73478 11 7 11H8.99999V13C8.99999 13.2652 9.10535 13.5196 9.29289 13.7071C9.48042 13.8946 9.73478 14 9.99999 14C10.2652 14 10.5196 13.8946 10.7071 13.7071C10.8946 13.5196 11 13.2652 11 13V11H13C13.2652 11 13.5196 10.8946 13.7071 10.7071C13.8946 10.5196 14 10.2652 14 9.99999C14 9.73478 13.8946 9.48042 13.7071 9.29289C13.5196 9.10535 13.2652 8.99999 13 8.99999Z" fill="white"/></g><defs><clipPath id="clip0_1086_1068"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>              <span>Type a topic to get a brief, evidence-based summary.</span>
              <MdKeyboardArrowDown color={'white'} size={16} className={`hero-dropdown-arrow ${openDropdown === 'search' ? 'rotated' : ''}`} />
            </button>
            {openDropdown === 'search' && (
              <div className="hero-expanded-examples">
                {dropdownExamples['search'].map((example, index) => (
                  <button
                    key={index}
                    className="hero-example-item"
                    onClick={() => handleExampleSelect(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="hero-disclaimer-text align-self-center py-3">
          <svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1.14282" y="1" width="14.7143" height="24" rx="7.35714" stroke="white" stroke-width="2"/>
          <circle cx="8.80954" cy="17.9524" r="1.85714" fill="white"/>
          </svg>
          <span className='px-2'>Scroll for more</span>
        </div>
        <WaitlistModal 
          show={showWaitlistModal} 
          onHide={() => setShowWaitlistModal(false)} 
          isInvestorInquiry={false}
        />
      </div>
    );
  }

  return (
    <div className="hero-chat-section">
      <div className="hero-messages">
        {messages.map((msg, idx) => {
          // Don't render assistant messages with empty content
          if (msg.role === 'assistant' && (!msg.content || !msg.content.trim())) {
            return null;
          }
          return (
          <React.Fragment key={idx}>
            {msg.role === 'user' ? (
              <div
                className={`hero-message ${msg.role === 'user' ? 'user' : 'ai'}`}
                ref={userMessagesStartRef}
              > 
                <div ref={messageStartRef} className="hero-user-message-content">{msg.content}</div>
              </div>
            ) : (
              <div className="hero-ai-message-wrapper">
                <div className="hero-ai-avatar-wrapper">
                  <img src={process.env.PUBLIC_URL + '/faviconM.svg'} alt="AI Avatar" className="hero-ai-avatar" />
                </div>
                <div
                  className={`hero-message ${msg.role === 'user' ? 'user' : 'ai'}`}
                > 
                  <div className="hero-markdown-content">
                    <Markdown components={{
                      p: ({node, ...props}) => <p className="hero-markdown-paragraph" {...props} />,
                      ul: ({node, ...props}) => <ul className="hero-markdown-list" {...props} />,
                      ol: ({node, ...props}) => <ol className="hero-markdown-ordered-list" {...props} />,
                      li: ({node, ...props}) => <li className="hero-markdown-list-item" {...props} />,
                      h1: ({node, ...props}) => <h1 className="hero-markdown-heading" {...props} />,
                      h2: ({node, ...props}) => <h2 className="hero-markdown-heading" {...props} />,
                      h3: ({node, ...props}) => <h3 className="hero-markdown-heading" {...props} />,
                      hr: ({node, ...props}) => <hr className="hero-markdown-divider" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="hero-markdown-blockquote" {...props} />,
                      pre: ({node, ...props}) => <pre className="hero-markdown-code-block" {...props} />
                    }}>
                      {msg.content}
                    </Markdown>
                  </div>
                </div>
              </div>
            )}
            <div className="hero-message-actions-row">
              <div className="hero-copy-btn-wrapper">
                {((msg.role === 'user' && hoveredUserMsg === idx) || msg.role === 'assistant' && !isStreaming) && (
                  <button
                    className="hero-copy-btn"
                    title="Copy message"
                    onClick={() => handleCopy(msg.content, idx)}
                    tabIndex={0}
                  >
                    <MdContentCopy size={18} />
                  </button>
                )}
                {copied[idx] && (
                  <span className="hero-copied-tooltip">Copied!</span>
                )}
              </div>
              {msg.role === 'assistant' && !isStreaming && (
                <>
                  <button
                    className="hero-like-btn"
                    title="Like"
                    onClick={() => handleFeedback(idx)}
                    tabIndex={0}
                    disabled={feedback[idx]}
                  >
                    <MdThumbUp size={18} />
                  </button>
                  <button
                    className="hero-dislike-btn"
                    title="Dislike"
                    onClick={() => handleFeedback(idx)}
                    tabIndex={0}
                    disabled={feedback[idx]}
                  >
                    <MdThumbDown size={18} />
                  </button>
                  {feedback[idx] && (
                    <span className="hero-feedback-thanks">Thank you for your feedback!</span>
                  )}
                </>
              )}
            </div>
            <div className='hero-message-actions-row-spacer' ref={messagesEndRef}/>
          </React.Fragment>
          );
        })}
        {loading && (
          <div className="hero-indicator" ref={messagesEndRef}>
            <div className="hero-typing-indicator-container">
              <span className="hero-typing-indicator">
                <span className="hero-dot"></span>
                <span className="hero-dot"></span>
                <span className="hero-dot"></span>
              </span>
            </div>
          </div>
        )}
      </div>
      <form className="hero-input-card-messages" onSubmit={handleSubmit}>
          <div className="hero-input-row">
            <input
              type="text"
              placeholder="Ask me anything..."
              className="hero-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
            />
            <button type="submit" className="hero-send-btn-messages" disabled={loading || !input.trim()}>
              {loading ? (
                <span className="hero-button-loading">
                  <div className="hero-spinner-small"></div>
                </span>
              ) : (
                  <FaArrowRightLong size={16} />
              )}
            </button>
          </div>
          <div className="hero-tab-buttons">
            {screens.map(bot => (
              <button
                type="button"
                key={bot.botId}
                className={`hero-tab-btn-messages${screenId === bot.id ? ' active' : ''}`}
                disabled={loading}
                onClick={() => onSwitchBot(bot.id)}
              >
                <div className="hero-tab-btn-content">
                  {bot.botImg}
                  {bot.label}
                </div>    
              </button>
            ))}
          </div>
        </form>
        <div className="hero-disclaimer-text-messages align-self-center py-2">
          MAIA is informational, not a substitute for professional judgement. <br/>
          <div className="py-2">
            <svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1.14258" y="1" width="14.7143" height="24" rx="7.35714" stroke="#939393" stroke-width="2"/>
              <circle cx="8.80929" cy="17.9524" r="1.85714" fill="#939393"/>
            </svg>
            <span className='px-2'>Scroll for more</span>
          </div>
        </div>
      <WaitlistModal 
        show={showWaitlistModal} 
        onHide={() => setShowWaitlistModal(false)} 
        isInvestorInquiry={false}
      />
    </div>
  );
}

export default HeroSection; 